import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play, RotateCcw, RotateCw } from 'lucide-react';

const SKIP_SECONDS = 15;

// One audio element per page; play(url) switches tracks, toggle() pauses/resumes.
export const useAudioPlayer = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audioRef.current = audio;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDuration = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onPlaying = () => setIsLoading(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);

    return () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
    };
  }, []);

  const startPlayback = (audio: HTMLAudioElement) => {
    audio.play().catch((error) => {
      console.error('Audio playback failed:', error);
      setIsLoading(false);
    });
  };

  const play = useCallback(
    (url: string) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (url !== src) {
        audio.src = url;
        setSrc(url);
        setCurrentTime(0);
        setDuration(0);
        setIsLoading(true);
      }
      startPlayback(audio);
    },
    [src]
  );

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    if (audio.paused) startPlayback(audio);
    else audio.pause();
  }, [src]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = Math.min(Math.max(time, 0), audio.duration);
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    setSrc(null);
    setIsLoading(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  return { src, isPlaying, isLoading, currentTime, duration, play, toggle, seek, stop };
};

export type AudioPlayer = ReturnType<typeof useAudioPlayer>;

export const formatTime = (time: number) => {
  if (!Number.isFinite(time) || time <= 0) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

interface PodcastPlayerProps {
  player: AudioPlayer;
  // Starts this track when nothing (or another track) is loaded.
  audioUrl: string;
  compact?: boolean;
}

const PodcastPlayer: React.FC<PodcastPlayerProps> = ({ player, audioUrl, compact = false }) => {
  const isCurrent = player.src === audioUrl;
  const isPlaying = isCurrent && player.isPlaying;
  const isLoading = isCurrent && player.isLoading;
  const currentTime = isCurrent ? player.currentTime : 0;
  const duration = isCurrent ? player.duration : 0;

  const handlePlayPause = () => {
    if (isCurrent) player.toggle();
    else player.play(audioUrl);
  };

  const mainButtonSize = compact ? 'w-11 h-11' : 'w-16 h-16';
  const mainIconSize = compact ? 'w-5 h-5' : 'w-7 h-7';
  const skipButtonClass =
    'p-2 rounded-full text-gray-600 dark:text-gray-300 hover:text-brandPurple dark:hover:text-brandBlue hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:pointer-events-none';

  return (
    <div className={`flex w-full ${compact ? 'items-center gap-3' : 'flex-col items-center gap-4'}`}>
      <div className="flex items-center gap-2 md:gap-4">
        <button
          type="button"
          onClick={() => player.seek(currentTime - SKIP_SECONDS)}
          disabled={!isCurrent}
          className={`${skipButtonClass} ${compact ? 'hidden sm:block' : ''}`}
          aria-label={`Back ${SKIP_SECONDS} seconds`}
        >
          <RotateCcw className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={handlePlayPause}
          className={`${mainButtonSize} flex items-center justify-center rounded-full bg-brandPurple text-white shadow-md hover:bg-opacity-90 transition-all duration-300 flex-shrink-0`}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <Loader2 className={`${mainIconSize} animate-spin`} />
          ) : isPlaying ? (
            <Pause className={mainIconSize} fill="currentColor" />
          ) : (
            <Play className={`${mainIconSize} ml-0.5`} fill="currentColor" />
          )}
        </button>
        <button
          type="button"
          onClick={() => player.seek(currentTime + SKIP_SECONDS)}
          disabled={!isCurrent}
          className={`${skipButtonClass} ${compact ? 'hidden sm:block' : ''}`}
          aria-label={`Forward ${SKIP_SECONDS} seconds`}
        >
          <RotateCw className="w-5 h-5" />
        </button>
      </div>

      <div className={`${compact ? 'hidden sm:flex' : 'flex'} items-center gap-3 w-full min-w-0`}>
        <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400 w-10 text-right">
          {formatTime(currentTime)}
        </span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={1}
          value={currentTime}
          onChange={(e) => player.seek(parseFloat(e.target.value))}
          disabled={!isCurrent || !duration}
          className="flex-1 min-w-0 h-1.5 accent-brandPurple cursor-pointer disabled:cursor-default"
          aria-label="Seek"
        />
        <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400 w-10">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};

export default PodcastPlayer;
